import React, { memo, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { navigate } from '@reach/router';
import lottie from 'lottie-web';
import Icon from '../Icon/Icon';
import SwipCard from '../Cards/SwipCard';
import TaskCard from '../TaskCard/TaskCard';
import Button from '../Button/Button';
import ButtonGroup from '../ButtonGroup';
import LoadingCircleIndicator from '../LoadingCircleIndicator/LoadingCircleIndicator';
import SanitizedHTML from 'react-sanitized-html';
import { MentionDate } from '../MentionPop/styles';
import { appDispatch, RootState } from '../../store/store';
import { searchTasks } from '../../store/tasks/tasksSlice';
import { IMention, ITask } from '../../store/tasks/tasksType';
import { ICompany } from '../../store/companies/companiesTypes';
import UserMixpanel from '../../utils/mixpanel/MixpanelClass';
import { convertDigitToProperFormat, isANumber } from '../../utils/number';
import { smartDateString } from '../../utils/date';
import {
  black,
  blue,
  boxShadow1,
  lightBlue,
  red,
} from '../../utils/style/colors';
import confettiAnimation from '../../assets/films/confetti.json';
import {
  TaskMentionsContainer,
  SectionTitle,
  TaskCardContainer,
  TaskContentHolder,
  TaskCardHolder,
  MentionsList,
  MentionRow,
  MentionStatusContainer,
} from './styles';
import { MiniItemWrapper } from '../TaskListView/styles';

interface ITaskMentionContainerProps {
  companyId: string | number;
  company: ICompany | null;
}

type TLoading = 'fetching' | '';
type MentionFilter = 'all' | 'direct' | 'other';

const TaskMentionContainer: React.FC<ITaskMentionContainerProps> = ({
  companyId,
  company,
}) => {
  const { t } = useTranslation('general');
  const thisUser = useSelector((state: RootState) => state.thisUser);
  const projects = useSelector(
    (state: RootState) => state.companies.data[companyId].projects
  );

  const isDarkTheme = useSelector(
    (state: RootState) => state.app.UI.themeMode === 'dark'
  );
  const cardRefs = useRef<Record<string, any>>({});
  const initialRef = useRef<number>(0);
  const mouseRef = useRef<{ x: number; y: number; time: number }>({
    x: 0,
    y: 0,
    time: 0,
  });
  const totalTasks = useRef<number>(0);
  const directTasks = useRef<number>(0);
  const otherTasks = useRef<number>(0);
  const totalMentionsDiv = useRef<HTMLElement | null>(null);
  const directMentionsDiv = useRef<HTMLElement | null>(null);
  const otherMentionsDiv = useRef<HTMLElement | null>(null);
  const counter = useRef<number>(0);
  const dismissedTasks = useRef<Set<number>>(new Set<number>());

  const [allTaskMentionList, setAllTaskMentionList] = useState<ITask[] | null>(
    null
  );
  const [taskMentionList, setTaskMentionList] = useState<ITask[] | null>(null);
  const [allReviewed, setAllReviewed] = useState<boolean>(false);
  const [loading, setLoading] = useState<TLoading>('');
  const [mentionFilter, setMentionFilter] = useState<MentionFilter>('direct');
  const [mentionCounts, setMentionCounts] = useState({
    all: 0,
    direct: 0,
    other: 0,
  });

  const handleGetMention = async () => {
    setLoading('fetching');
    const taskResponse = await appDispatch(
      searchTasks({
        params: {
          companyId: Number(companyId),
          mentioned: true,
          from: 0,
          count: 200,
        },
      })
    );
    const lastMentionMap = new Map<number, number>();
    taskResponse.tasks.forEach((task: ITask) => {
      lastMentionMap.set(
        task.id,
        Math.max(...(task.mentions?.map((m) => m.creationDate) || [0]))
      );
    });
    const sortedTasks = taskResponse.tasks.sort(
      (a: ITask, b: ITask) =>
        (lastMentionMap.get(a.id) || 0) - (lastMentionMap.get(b.id) || 0)
    );
    setAllTaskMentionList(sortedTasks);

    // Calculate task counts
    const counts = { all: sortedTasks.length, direct: 0, other: 0 };
    sortedTasks.forEach((task: ITask) => {
      const hasDirectMention = task.mentions.some(
        (mention: IMention) => mention.directAddress
      );
      if (hasDirectMention) {
        counts.direct++;
      } else {
        counts.other++;
      }
    });
    setMentionCounts(counts);
    totalTasks.current = counts.all;
    directTasks.current = counts.direct;
    otherTasks.current = counts.other;

    // Set initial filter
    const initialFilter = counts.direct > 0 ? 'direct' : 'all';
    setMentionFilter(initialFilter);
    filterTasks(sortedTasks, initialFilter);

    setLoading('');
  };

  const filterTasks = (tasks: ITask[], filter: MentionFilter) => {
    let filteredTasks: ITask[];
    if (filter === 'all') {
      filteredTasks = tasks;
    } else if (filter === 'direct') {
      filteredTasks = tasks.filter((task: ITask) =>
        task.mentions.some((mention: IMention) => mention.directAddress)
      );
    } else {
      filteredTasks = tasks.filter(
        (task: ITask) =>
          !task.mentions.some((mention: IMention) => mention.directAddress)
      );
    }
    filteredTasks = filteredTasks.filter(
      (task: ITask) => !dismissedTasks.current.has(task.id)
    );
    setTaskMentionList(filteredTasks);
    setAllReviewed(filteredTasks.length === 0);
  };

  const handleFilterChange = (filter: string) => {
    const newFilter = filter as MentionFilter;
    setMentionFilter(newFilter);
    if (allTaskMentionList) {
      filterTasks(allTaskMentionList, newFilter);
    }
  };

  const handleClickOnTask = (task: ITask, _: any) => {
    const mx = UserMixpanel.getInstance();
    mx.visitedTasksCountHandler();
    if (isANumber(task.id)) {
      mx.handleSetVisitedTaskCountByTemplates(task.taskName);
    }
    let url = `/t/${companyId}/home/editor`;
    if (task.id) {
      url = `${url}/${task.id}/info`;
    }
    navigate(url);
  };

  const swipe = (dir: 'left' | 'right') => {
    const ref = cardRefs.current[taskMentionList![counter.current]?.id];
    if (ref && typeof ref.swipe === 'function') {
      ref.swipe(dir);
    } else {
      console.warn('swipe method not found', ref);
    }
  };

  const handleBeforeSwipe = (_: number) => {
    if (taskMentionList && counter.current < 0) return;
  };

  const handleAfterSwipe = () => {
    const currentTask = taskMentionList![counter.current];
    dismissedTasks.current.add(currentTask.id);
    const hasDirectMention = currentTask.mentions.some(
      (mention: IMention) => mention.directAddress
    );

    totalTasks.current = totalTasks.current - 1;
    if (totalMentionsDiv.current) {
      totalMentionsDiv.current.innerHTML = convertDigitToProperFormat(
        totalTasks.current
      );
    }

    if (hasDirectMention) {
      directTasks.current = directTasks.current - 1;
      if (directMentionsDiv.current) {
        directMentionsDiv.current.innerHTML = convertDigitToProperFormat(
          directTasks.current
        );
      }
    } else {
      otherTasks.current = otherTasks.current - 1;
      if (otherMentionsDiv.current) {
        otherMentionsDiv.current.innerHTML = convertDigitToProperFormat(
          otherTasks.current
        );
      }
    }
    counter.current = counter.current - 1;
    if (counter.current < 0 && initialRef.current > 0) {
      setTimeout(() => {
        const anim = lottie.loadAnimation({
          container: document.getElementById('confetti-container')!,
          renderer: 'svg',
          loop: false,
          autoplay: true,
          animationData: confettiAnimation,
        });
        setTimeout(() => {
          anim.destroy();
        }, 2400);
        setAllReviewed(true);
      }, 1000);
    }
  };

  useEffect(() => {
    handleGetMention();
  }, []);

  useEffect(() => {
    initialRef.current += 1;
  }, []);

  useEffect(() => {
    if (taskMentionList && taskMentionList.length > 0) {
      counter.current = taskMentionList.length - 1;
    }
  }, [taskMentionList]);

  useEffect(() => {
    const allMentionsCounter = company
      ? (company.mentionCount?.['directAddress'] || 0) +
        (company.mentionCount?.['info'] || 0)
      : 0;
    // totalMentions.current = allMentionsCounter;
    // directAddresslMentions.current =
    //   company?.mentionCount?.['directAddress'] || 0;
    // infoMentions.current = company?.mentionCount?.['info'] || 0;
    if (allMentionsCounter === 0) setAllReviewed(true);
  }, [company]);

  useEffect(() => {
    // Update ref divs when counts change
    if (totalMentionsDiv.current) {
      totalMentionsDiv.current.innerHTML = convertDigitToProperFormat(
        mentionCounts.all
      );
    }
    if (directMentionsDiv.current) {
      directMentionsDiv.current.innerHTML = convertDigitToProperFormat(
        mentionCounts.direct
      );
    }
    if (otherMentionsDiv.current) {
      otherMentionsDiv.current.innerHTML = convertDigitToProperFormat(
        mentionCounts.other
      );
    }
  }, [mentionCounts]);

  const buttonGroupItems = {
    all: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Icon type="at-sign" style={{ fontSize: '1rem' }} color={blue} />
        <span>همه</span>(
        <span ref={totalMentionsDiv}>
          {convertDigitToProperFormat(mentionCounts.all)}
        </span>
        )
      </div>
    ),
    direct: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Icon type="at-sign" style={{ fontSize: '1rem' }} color={red} />
        <span>خطاب به شما</span>(
        <span ref={directMentionsDiv}>
          {convertDigitToProperFormat(mentionCounts.direct)}
        </span>
        )
      </div>
    ),
    other: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Icon type="info" style={{ fontSize: '1rem' }} color={lightBlue} />
        <span>سایر</span>(
        <span ref={otherMentionsDiv}>
          {convertDigitToProperFormat(mentionCounts.other)}
        </span>
        )
      </div>
    ),
  };

  return (
    <TaskMentionsContainer className={'companyHomepage-cards'}>
      <SectionTitle>{t('events')}</SectionTitle>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          width: '320px',
          margin: 'auto',
        }}
      >
        <ButtonGroup
          items={buttonGroupItems}
          onSelect={handleFilterChange}
          selected={mentionFilter}
          border={false}
        />
      </div>
      <TaskCardContainer
        onMouseDown={(e: any) => {
          mouseRef.current = {
            x: e.clientX,
            y: e.clientY,
            time: new Date().getTime(),
          };
        }}
        onMouseUp={(e: any) => {
          if (
            Math.abs(e.clientX - mouseRef.current.x) <= 10 &&
            Math.abs(e.clientY - mouseRef.current.y) <= 10 &&
            Math.abs(new Date().getTime() - mouseRef.current.time) <= 500
          ) {
            handleClickOnTask(taskMentionList![counter.current], e);
          }
        }}
        onTouchStart={(e: any) => {
          const touch = e.touches[0];
          mouseRef.current = {
            x: touch.clientX,
            y: touch.clientY,
            time: new Date().getTime(),
          };
        }}
        onTouchEnd={(e: any) => {
          const touch = e.changedTouches[0];
          if (
            Math.abs(touch.clientX - mouseRef.current.x) <= 10 &&
            Math.abs(touch.clientY - mouseRef.current.y) <= 10 &&
            Math.abs(new Date().getTime() - mouseRef.current.time) <= 500
          ) {
            handleClickOnTask(taskMentionList![counter.current], e);
          }
        }}
      >
        {loading === 'fetching' ? (
          <LoadingCircleIndicator />
        ) : allReviewed ? (
          <div
            className="w-full h-full text-center font-bold flex justify-center items-center"
            style={{ color: black }}
          >
            {t('reviewedAllMentions')}
          </div>
        ) : taskMentionList?.length ? (
          <>
            {taskMentionList?.length > 1 && (
              <MiniItemWrapper
                style={{
                  position: 'absolute',
                  top: '12px',
                  left: '20px',
                  right: '20px',
                  zIndex: 0,
                  borderRadius: '2rem',
                  height: '100%',
                  width: 'calc(100% - 40px)',
                  boxShadow: boxShadow1,
                }}
                isDark={isDarkTheme}
              ></MiniItemWrapper>
            )}
            {taskMentionList?.length > 0 && (
              <MiniItemWrapper
                style={{
                  position: 'absolute',
                  top: '8px',
                  left: '10px',
                  right: '10px',
                  zIndex: 0,
                  /* background-color: red, */
                  borderRadius: '2rem',
                  height: '100%',
                  width: 'calc(100% - 20px)',
                  boxShadow: `${boxShadow1}`,
                }}
                isDark={isDarkTheme}
              ></MiniItemWrapper>
            )}
            <MiniItemWrapper
              style={{
                position: 'absolute',
                top: '0px',
                left: '0px',
                right: '0px',
                zIndex: 0,
                /* background-color: red, */
                borderRadius: '2rem',
                height: '100%',
                width: 'calc(100%)',
                boxShadow: `${boxShadow1}`,
              }}
              isDark={isDarkTheme}
            ></MiniItemWrapper>
            <div style={{ position: 'relative' }}>
              {taskMentionList.map((task: ITask, index: number) => {
                const project = projects?.[task.projectId];
                return (
                  <SwipCard
                    ref={(el) => {
                      if (el) cardRefs.current[task.id] = el;
                      else delete cardRefs.current[task.id];
                    }}
                    key={task.id}
                    item={{ id: task.id, name: task.taskName }}
                    isCurrentItemIsOnTop={index === counter.current}
                    handleBeforeSwipe={handleBeforeSwipe}
                    handleAfterSwipe={handleAfterSwipe}
                  >
                    <TaskContentHolder>
                      <TaskCardHolder>
                        <TaskCard
                          task={task}
                          companyId={companyId!}
                          project={project!}
                          templateSelected={false}
                        />
                      </TaskCardHolder>
                      <MentionsList>
                        {(mentionFilter === 'direct'
                          ? [
                              ...task.mentions.filter((m) => m.directAddress),
                              ...task.mentions.filter((m) => !m.directAddress),
                            ]
                          : task.mentions
                        ).map((mention: IMention) => {
                          const color =
                            mention.event.type === 'CommentEvent'
                              ? red
                              : mention.directAddress
                                ? blue
                                : lightBlue;
                          const text = (
                            mention.directAddress
                              ? mention.event.objectText
                              : mention.event.text
                          )
                            .replaceAll(
                              RegExp(
                                `@\\[U${thisUser?.userId}]\\(([^)]+)\\)`,
                                'g'
                              ),
                              `<b style="color:${blue}">@$1</b>`
                            )
                            .replaceAll(
                              /@\[U[0-9]+]\(([^)]+)\)/g,
                              `<b style="color:${lightBlue}">@$1</b>`
                            );
                          return (
                            <MentionRow
                              key={`${mention.taskId}-${mention.id}`}
                              color={color}
                            >
                              <Icon
                                type={
                                  mention.event.type === 'CommentEvent'
                                    ? 'message-circle'
                                    : mention.directAddress
                                      ? 'at-sign'
                                      : 'info'
                                }
                                color={color}
                                style={{ height: '100%', padding: '5px 0 0' }}
                              />

                              <div style={{ flexGrow: '1', color: black }}>
                                <SanitizedHTML
                                  allowedAttributes={{ b: ['style'] }}
                                  allowedTags={['b']}
                                  html={text}
                                />
                              </div>
                              <MentionDate style={{ minWidth: '80px' }}>
                                {smartDateString(mention.creationDate, true)}
                              </MentionDate>
                            </MentionRow>
                          );
                        })}
                      </MentionsList>
                    </TaskContentHolder>
                  </SwipCard>
                );
              })}
            </div>
          </>
        ) : (
          <div
            className="flex w-full text-center items-center justify-center h-full font-bold"
            style={{ color: black }}
          >
            {t('nothingFounded')}
          </div>
        )}
      </TaskCardContainer>
      <MentionStatusContainer>
        <Button
          color={'orange-outline'}
          onClick={() => swipe('right')}
          disabled={
            loading === 'fetching' ||
            counter.current < 0 ||
            mentionCounts[mentionFilter] === 0
          }
        >
          <div style={{ marginTop: '0.25rem', paddingInlineEnd: '0.5rem' }}>
            {' → '}
          </div>
          <div>{t('remindMeLater')}</div>
        </Button>
        <Button
          color={'green-outline'}
          onClick={() => swipe('left')}
          disabled={
            loading === 'fetching' ||
            counter.current < 0 ||
            mentionCounts[mentionFilter] === 0
          }
        >
          <div>{t('seen')}</div>
          <div style={{ marginTop: '0.25rem', paddingInlineStart: '0.5rem' }}>
            {' ← '}
          </div>
        </Button>
      </MentionStatusContainer>
    </TaskMentionsContainer>
  );
};

export default memo(TaskMentionContainer, (prevProps, nextProps) => {
  return (
    prevProps.companyId === nextProps.companyId &&
    prevProps.company?.status === nextProps.company?.status &&
    prevProps.company?.stub === nextProps.company?.stub &&
    prevProps.company?.mentionCount === nextProps.company?.mentionCount
  );
});
